/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import React from "react";

import {
	BackgroundContainer,
	ButtonList,
	CosmosButtonProps,
	CosmosText,
	EdsIcon,
	EdsIconType,
	ResponsiveContainer,
	SectionContainer,
	TitleDescStack,
} from "@components/elements";

import { NumberCalloutSectionFragment } from "@cosmos/hygraph-schema";
import { extractBackgroundContainerProps, extractSectionContainerProps } from "@components/utils/section.utils";
import { css, cx } from "@panda/css";
import { Box, VStack } from "@panda/jsx";
import { NumberCalloutList } from "./NumberCalloutList.molecule";

export interface NumberCalloutSectionProps extends Omit<NumberCalloutSectionFragment, "__typename" | "id"> {
	sectionIndex?: number;
}

const sectionStyles = css({
	minHeight: "100%",
	md: {
		minHeight: "540px",
	},
	xl: {
		minHeight: "650px",
	},
	"&&": { smDown: { alignItems: "start" } },
});

const pageHeightStyles = css({
	height: "calc(100vh - 72px)",
	overflowX: "clip",
	contentVisibility: "unset",
	minHeight: "540px",
	smDown: {
		height: "100%",
		minHeight: "calc(100vh - 72px)",
	},
});

const iconStyles = css({
	"& svg": {
		height: "eds.dimension.80!important", //override EDS style
		width: "eds.dimension.80!important",
	},
});

export const NumberCalloutSection = ({
	config,
	eyebrow,
	title,
	desc,
	callouts,
	numberCalloutVariant,
	timeAndDate,
	buttons,
	sectionIndex,
	icon,
	iconGradient,
	titleGradient,
}: NumberCalloutSectionProps): React.JSX.Element => {
	const isFirstSection = sectionIndex === 0;
	const isPageHeight = isFirstSection && config?.backgroundFullHeight;

	const cosmosButtons: CosmosButtonProps[] =
		buttons?.map((button) => {
			return {
				...button,
				size: "lg",
			};
		}) || [];

	return (
		<BackgroundContainer
			{...extractBackgroundContainerProps(config)}
			position="relative"
			className={isPageHeight ? pageHeightStyles : ""}
		>
			<ResponsiveContainer zIndex="2" h="100%">
				<SectionContainer
					{...extractSectionContainerProps(config)}
					height="100%"
					display="flex"
					alignItems="center"
					className={sectionStyles}
				>
					<VStack
						alignItems="start"
						gap="eds.dimension.32"
						width="100%"
						maxWidth="614px"
						paddingTop={{ xs: isPageHeight ? "eds.dimension.64" : undefined, xsDown: "0" }}
						className={cx("number-callout-section")}
					>
						{icon ? (
							<Box className={iconStyles}>
								<EdsIcon size="xl" icon={icon as EdsIconType} gradient={iconGradient} />
							</Box>
						) : null}
						{eyebrow ? <CosmosText variant="eyebrowLG" text={eyebrow} /> : null}
						{title || desc ? (
							<TitleDescStack
								stack={{ gap: "eds.dimension.16" }}
								gradient={titleGradient}
								title={{
									...title,
									as: isFirstSection ? "h1" : "h2",
									variant: isFirstSection ? "heading2XL" : "headingXL",
								}}
								description={{ html: desc, color: "eds.text.primary" }}
							/>
						) : null}
						{callouts?.length || timeAndDate || numberCalloutVariant ? (
							<NumberCalloutList
								callouts={callouts}
								numberCalloutVariant={numberCalloutVariant}
								timeAndDate={timeAndDate}
							/>
						) : null}
						{cosmosButtons?.length ? <ButtonList buttons={cosmosButtons} /> : null}
					</VStack>
				</SectionContainer>
			</ResponsiveContainer>
		</BackgroundContainer>
	);
};
